import { StreemAPI } from '@streem/api';
import { invariant } from '@streem/toolbox';
import { shareReplay, combineLatestWith, switchMap } from 'rxjs/operators';
import { StreemError } from '../../streem_error';
import { logger } from '../../util/logger';
import { filterEmptyArray } from '../../util/rx/filter_empty_array';
import { firstFromGroup } from '../../util/rx/first_from_group';
import { accumulateAndMapWallEvents, excludeByProperty, filterByProperty, findInGroup, } from '../wall/utils';
export class UserController {
    constructor(config) {
        this.config = config;
        const roomWallItem = this.config.roomWall.itemsByType.pipe(findInGroup('room'), accumulateAndMapWallEvents('room'));
        const userWallItems = this.config.roomWall.itemsByType.pipe(findInGroup('user'), accumulateAndMapWallEvents('user'));
        // The room wall item will always contain all the participants,
        // while the user wall items are added at certain points in the call lifecycle.
        // Meaning if the customer ends call early, the expert user may not have had a chance to be added to the wall.
        // we combine both wall items to build our UserInfo[] to cover this case so all users are present even if its with limited info
        this.users = roomWallItem.pipe(combineLatestWith(userWallItems), switchMap(async (value) => {
            const room = value[0][0];
            const users = value[1];
            const userInfo = (room.participants || []).map(async (participant) => {
                const userWallItem = users.find(it => it.ownerUserId === participant.userSid);
                try {
                    const { user: userData } = await StreemAPI.users.getUser(participant.userSid);
                    return {
                        ...userWallItem,
                        ownerUserId: participant.userSid,
                        id: participant.userSid,
                        name: userWallItem?.name ?? '',
                        // Note: onsite streems do not post the photo url
                        photoURL: userWallItem?.avatarUrl ?? userData?.avatarUrl ?? '',
                        // Hacky solution until we actually set the customer phone number for new customers
                        phone: userData?.phone ||
                            (userData?.externalUserId?.startsWith('+')
                                ? userData?.externalUserId
                                : undefined),
                    };
                }
                catch (error) {
                    logger.warn('Failed to fetch additional user information: continuing without');
                    return {
                        ...userWallItem,
                        id: participant.userSid,
                        name: userWallItem?.name ?? '',
                        photoURL: userWallItem?.avatarUrl ?? '',
                    };
                }
            });
            return (await Promise.all(userInfo));
        }), filterEmptyArray(), shareReplay(1));
        this.localUser = this.users.pipe(filterByProperty('ownerUserId', config.context.localUserId?.toString() ?? 'NEVER'), firstFromGroup(), shareReplay(1));
        this.remoteUsers = this.users.pipe(excludeByProperty('ownerUserId', config.context.localUserId?.toString() ?? 'NEVER'), shareReplay(1));
        this.remoteUser = this.users.pipe(filterByProperty('ownerUserId', config.context.remoteUserId?.toString()), firstFromGroup());
    }
    getUser(userId) {
        return this.users.pipe(filterByProperty('id', userId.toString()), firstFromGroup());
    }
    async addLocalUserToWall() {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when adding a local user to the wall');
        const { user } = await StreemAPI.users.getUser(this.config.context.localUserId.toString());
        if (user && !user.name) {
            user.name = 'Streem User';
        }
        if (!user || !user.sid || !user.name) {
            throw new StreemError('Unable to get user details.');
        }
        const userInfo = {
            name: user.name,
            avatarUrl: user.avatarUrl,
        };
        logger.info('User has been added to the wall', this.config.context.localUserId.toString());
        this.config.roomWall.addUserItem(this.config.context.localUserId, userInfo);
    }
}
//# sourceMappingURL=user_controller.js.map